import React from 'react';
import {
  AppstoreOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { ChatRole } from '../../api/agentChat/interface';
import type { ClaimFile } from '../../api/files/interface';
import type { Task } from '../../api/tasks/interface';
import { useEscDossierContext } from '../DossierContext';
import MessageFeedback from '../Feedback/MessageFeedback';
import { EscMarkdown } from '../Markdown/Markdown';
import { useCitationHandler } from '../utils';
import styles from './Chat.module.scss';

dayjs.extend(utc);

// `escalation_id` is kept in the split so historical messages that mention a
// (now-removed) escalation still render as an inert chip rather than raw text.
const MENTION_SPLIT_RE = /(@(?:escalation_id|file_id|task_id):[\w-]+)/g;

export type MentionClickHandler = (type: 'file' | 'task', id: string) => void;

export function renderUserText(
  text: string,
  files: ClaimFile[],
  tasks: Task[],
  onMentionClick?: MentionClickHandler
): React.ReactNode {
  const parts = text.split(MENTION_SPLIT_RE);
  return parts.map((part, i) => {
    const escMatch = part.match(/^@escalation_id:([\w-]+)$/);
    if (escMatch) {
      // Legacy escalation mention — render as an inert chip (escalations were
      // folded into tasks; there's nothing to open).
      return (
        <span
          key={i}
          className={`${styles.mentionChip} ${styles.mentionChipTask}`}
        >
          <CheckSquareOutlined className={styles.mentionChipIcon} />
          <span className={styles.mentionChipLabel}>{escMatch[1]}</span>
        </span>
      );
    }
    const fileMatch = part.match(/^@file_id:([\w-]+)$/);
    if (fileMatch) {
      const file = files.find((f) => f.file_id === fileMatch[1]);
      return (
        <span
          key={i}
          className={`${styles.mentionChip} ${styles.mentionChipFile}`}
          style={onMentionClick ? { cursor: 'pointer' } : undefined}
          onClick={
            onMentionClick
              ? () => onMentionClick('file', fileMatch[1])
              : undefined
          }
        >
          <FileTextOutlined className={styles.mentionChipIcon} />
          <span className={styles.mentionChipLabel}>
            {file ? file.file_name : fileMatch[1]}
          </span>
        </span>
      );
    }
    const taskMatch = part.match(/^@task_id:([\w-]+)$/);
    if (taskMatch) {
      const task = tasks.find((t) => t.id === taskMatch[1]);
      return (
        <span
          key={i}
          className={`${styles.mentionChip} ${styles.mentionChipTask}`}
          style={onMentionClick ? { cursor: 'pointer' } : undefined}
          onClick={
            onMentionClick
              ? () => onMentionClick('task', taskMatch[1])
              : undefined
          }
        >
          <CheckSquareOutlined className={styles.mentionChipIcon} />
          <span className={styles.mentionChipLabel}>
            {task ? task.title : taskMatch[1]}
          </span>
        </span>
      );
    }
    return part;
  });
}

interface Props {
  role: ChatRole;
  text: string;
  files?: ClaimFile[];
  tasks?: Task[];
  onMentionClick?: MentionClickHandler;
  createdAt?: string;
  streaming?: boolean;
  actions?: React.ReactNode;
  onCitationClick?: (fileId: string, page?: number) => void;
  messageId?: string;
  highlighted?: boolean;
  artifactId?: string | null;
  artifactName?: string | null;
  dossierId?: string;
  threadId?: string;
  query?: string;
}

function tightenListSpacing(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  const isListItem = (s: string) => /^\s*([-*+]|\d+\.)\s/.test(s);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') {
      const prev = lines[i - 1];
      const next = lines[i + 1];
      if (
        prev !== undefined &&
        next !== undefined &&
        isListItem(prev) &&
        isListItem(next)
      ) {
        continue;
      }
    }
    out.push(line);
  }
  return out.join('\n');
}

const MessageBubble: React.FC<Props> = ({
  role,
  text,
  createdAt,
  streaming,
  actions,
  onCitationClick,
  messageId,
  highlighted,
  files = [],
  tasks = [],
  onMentionClick,
  artifactId,
  artifactName,
  dossierId,
  threadId,
  query,
}) => {
  const { onArtifactClick, onWikiClick } = useEscDossierContext();
  const isUser = role === 'USER';

  const onFileClick = React.useCallback(
    (fileId: string, page?: number) => onCitationClick?.(fileId, page),
    [onCitationClick]
  );
  const onKnowledgeClick = React.useCallback(
    (kbPath: string, anchor?: string) =>
      onWikiClick(`knowledge/${kbPath}${anchor ? `#${anchor}` : ''}`),
    [onWikiClick]
  );
  const handleCitationClick = useCitationHandler(onFileClick, onKnowledgeClick);

  const time = createdAt ? dayjs.utc(createdAt).local().format('h:mm A') : '';

  if (isUser) {
    return (
      <div
        className={clsx(
          styles.userRow,
          highlighted && styles.messageHighlighted
        )}
        data-message-id={messageId}
      >
        <div className={styles.userBubble}>
          {renderUserText(text, files, tasks, onMentionClick)}
        </div>
        {time && <div className={styles.userTime}>{time}</div>}
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.owlRow, highlighted && styles.messageHighlighted)}
      data-message-id={messageId}
    >
      {text && (
        <div className={clsx(styles.owlText, streaming && styles.owlStreaming)}>
          <EscMarkdown
            text={tightenListSpacing(text)}
            customCitationHandler={handleCitationClick}
            showTooltip={false}
            hidePage
            stateDb
          />
        </div>
      )}
      {artifactId && (
        <button
          type="button"
          className={styles.artifactChip}
          onClick={() => onArtifactClick(artifactId)}
        >
          {streaming ? (
            <LoadingOutlined className={styles.artifactChipIcon} />
          ) : (
            <AppstoreOutlined className={styles.artifactChipIcon} />
          )}
          {artifactName || 'View artifact'}
        </button>
      )}
      {!streaming && text && (
        <MessageFeedback
          answer={text}
          query={query}
          dossierId={dossierId}
          threadId={threadId}
          messageId={messageId}
        />
      )}
      {actions && <div className={styles.actionSlot}>{actions}</div>}
    </div>
  );
};

export default MessageBubble;
